import api from "../api/api";
import { V2_BASE_URL } from "../api/bases";

/*
 * Dashboard Refresh with progress (practiceFeatures().refreshProgress: 9928),
 * 2026-10-29. The refresh pulls Athena day by day and used to answer only at
 * the end (minutes), so a long refresh looked like a failure.
 *
 * v2 POST /v1/{p}/appointments/refresh/start starts the refresh as a server
 * job (or joins the one already running for the practice) and answers at once
 * with its job_id; GET /v1/{p}/appointments/refresh/status/{job_id} reports
 * its stage, "day i of n", and the counts. Records hold counts only.
 *
 * Pinned to v2 (the routes exist there only). Only when v2 says it has no
 * job for this practice -- the routes are not deployed yet (router 404/405),
 * the practice is not enabled (403), or an answer without a job_id -- the
 * caller falls back to the old v1 refresh/apt call (RefreshJobUnavailable).
 * A start that got NO answer (network, timeout) may still have started a job
 * on the server, so it never falls back: it asks GET refresh/status (the
 * practice's latest job) and follows a running or just-started job, else
 * reports the error (review 10-30: no second, unprogressed v1 refresh).
 *
 * Deploy order: v2 with the routes first, then this UI. This UI alone keeps
 * working through the fallback.
 */
const V2 = { baseURL: V2_BASE_URL, _gw: "v2-pinned" };
export const POLL_MS = 1500;
const MAX_POLL_ERRORS = 6;
const START_TIMEOUT_MS = 20000;
const RECENT_JOB_SECONDS = 180;

export class RefreshJobUnavailable extends Error {
  constructor(message = "refresh job route unavailable") {
    super(message);
    this.name = "RefreshJobUnavailable";
  }
}

export class RefreshJobLost extends Error {
  constructor(message = "lost contact with the refresh") {
    super(message);
    this.name = "RefreshJobLost";
  }
}

const hasDept = (d) => d != null && d !== "";

// The practice's latest job (GET refresh/status), or null.
export async function getCurrentRefreshJob(practiseid, departmentId) {
  const res = await api.get(`/v1/${practiseid}/appointments/refresh/status`, {
    ...V2,
    timeout: 15000,
    ...(hasDept(departmentId) ? { params: { department_id: String(departmentId) } } : {}),
  });
  return res && res.job ? { job: res.job, age: Number(res.age_seconds) } : null;
}

export async function startRefreshJob(practiseid, departmentId) {
  let res;
  try {
    res = await api.post(
      `/v1/${practiseid}/appointments/refresh/start`,
      hasDept(departmentId) ? { department_id: String(departmentId) } : {},
      { ...V2, timeout: START_TIMEOUT_MS },
    );
  } catch (error) {
    const status = error?.response?.status;
    if (status === 403 || status === 404 || status === 405) throw new RefreshJobUnavailable();
    if (error?.response) throw error;
    // No answer: the server may have started the job anyway -- look for it, never fall back.
    let current = null;
    try {
      current = await getCurrentRefreshJob(practiseid, departmentId);
    } catch (e) {
      current = null;
    }
    if (current && current.job.job_id &&
        (current.job.state === "running" || (current.age >= 0 && current.age < RECENT_JOB_SECONDS))) {
      return { job_id: current.job.job_id, joined: true, job: current.job };
    }
    throw new RefreshJobLost("no answer from the server");
  }
  if (!res || !res.job_id || !res.job) throw new RefreshJobUnavailable("no job in the answer");
  return res;
}

export const getRefreshJob = (practiseid, jobId) =>
  api.get(`/v1/${practiseid}/appointments/refresh/status/${encodeURIComponent(jobId)}`, {
    ...V2,
    timeout: 15000,
  });

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Starts (or joins) the job and polls it to its end. onUpdate(job, { joined })
// after every answer. Resolves to the final job (state "done" or "failed"), or
// null when shouldStop() turned true (the dashboard unmounted; the server job
// keeps running). Rejects with RefreshJobUnavailable (use the old call) or
// RefreshJobLost (the status answers stopped).
export async function runRefreshJob(practiseid, departmentId, onUpdate = () => {}, opts = {}) {
  const { sleep = defaultSleep, intervalMs = POLL_MS, shouldStop = () => false } = opts;
  const started = await startRefreshJob(practiseid, departmentId);
  const jobId = started.job_id;
  let job = started.job;
  onUpdate(job, { joined: Boolean(started.joined) });
  let errors = 0;
  while (job && job.state === "running") {
    await sleep(intervalMs);
    if (shouldStop()) return null;
    try {
      const res = await getRefreshJob(practiseid, jobId);
      if (!res || !res.job) throw new Error("empty status answer");
      job = res.job;
      errors = 0;
      onUpdate(job, { joined: Boolean(started.joined) });
    } catch (error) {
      if (error?.response?.status === 404) throw new RefreshJobLost("the refresh is no longer known");
      errors += 1;
      if (errors >= MAX_POLL_ERRORS) throw new RefreshJobLost();
    }
  }
  return job;
}

const n = (v) => Number(v) || 0;
const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

// The line the banner shows for a job (null = just clicked, nothing back yet).
export function refreshStageText(job) {
  if (!job) return "Starting refresh…";
  const c = job.counts || {};
  switch (job.stage) {
    case "queued":
    case "started":
      return "Starting refresh…";
    case "fetching":
      return n(job.days) > 1 && n(job.day) > 0
        ? `Checking Athena… day ${n(job.day)} of ${n(job.days)}`
        : "Checking Athena…";
    case "resolving":
      return "Matching appointments to locations…";
    case "cancelling":
      return "Checking for cancellations…";
    case "inserting":
      return n(c.new) > 0
        ? `Adding ${plural(n(c.new), "new appointment")}… (${n(job.processed)} of ${n(job.total)} checked)`
        : `Checking appointments… ${n(job.processed)} of ${n(job.total)}`;
    case "backfilling":
      return "Finishing up…";
    case "done":
      return refreshDoneText(job);
    case "failed":
    case "stopped":
      return job.message || "The refresh could not finish. Please try again.";
    default:
      return "Refreshing appointments…";
  }
}

export function refreshDoneText(job) {
  const c = (job && job.counts) || {};
  let text = `Done: ${n(c.new)} new, ${n(c.updated)} updated, ${n(c.cancelled)} removed`;
  if (n(c.errors) > 0) text += `; ${n(c.errors)} could not be read from Athena`;
  return text;
}

// Determinate progress 0..100. The day-by-day Athena fetch is most of the wait,
// then the per-appointment pass; the short stages get fixed points.
export function refreshProgressPercent(job) {
  if (!job) return 2;
  switch (job.stage) {
    case "queued":
      return 2;
    case "started":
      return 4;
    case "fetching":
      return n(job.days) > 0 ? Math.round(5 + (55 * Math.min(n(job.day), n(job.days))) / n(job.days)) : 5;
    case "resolving":
      return 61;
    case "cancelling":
      return 63;
    case "inserting":
      return n(job.total) > 0 ? Math.round(64 + (32 * Math.min(n(job.processed), n(job.total))) / n(job.total)) : 64;
    case "backfilling":
      return 97;
    case "done":
      return 100;
    default:
      return 0;
  }
}
